import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { marketplaceApi } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import MarketplaceHeader from "../components/MarketplaceHeader";
import FloatingTools from "../components/FloatingTools";
import Footer from "../components/Footer";
import { clearPurchaseDraft, loadPurchaseDraft, savePurchaseDraft } from "../utils/purchaseDrafts";

const REQUEST_DRAFT_KEY = "request-quote";

export default function RequestQuote() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [status, setStatus] = useState("");
  const [form, setForm] = useState({
    item: "",
    category: "",
    quantity: 1,
    delivery_area: "",
    budget: "",
    message: "",
  });

  useEffect(() => {
    const draft = loadPurchaseDraft(REQUEST_DRAFT_KEY);
    if (draft) setForm((current) => ({ ...current, ...draft }));
  }, []);

  useEffect(() => {
    let mounted = true;
    marketplaceApi.categories()
      .then((res) => {
        if (!mounted) return;
        const parentOnly = (Array.isArray(res.data) ? res.data : []).filter((item) => !item.parent && !item.parent_name);
        setCategories(parentOnly);
      })
      .catch(() => setCategories([]));
    return () => { mounted = false; };
  }, []);

  const selectedCategory = useMemo(() => categories.find((cat) => String(cat.id) === String(form.category) || cat.code === form.category), [categories, form.category]);

  function update(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function submit(event) {
    event.preventDefault();
    if (!isAuthenticated) {
      savePurchaseDraft(REQUEST_DRAFT_KEY, form);
      navigate(`/login?next=${encodeURIComponent("/request")}`);
      return;
    }
    if (!form.item.trim()) {
      setStatus("Please describe what you want to source.");
      return;
    }
    setStatus("Sending request...");
    const body = [
      `SOURCE REQUEST: ${form.item}`,
      selectedCategory ? `CATEGORY: ${selectedCategory.name}` : null,
      form.budget ? `BUDGET / TARGET PRICE: ${form.budget}` : null,
      form.message ? `BUYER NOTES: ${form.message}` : null,
    ].filter(Boolean).join("\n");

    try {
      await marketplaceApi.createProductEnquiry({
        product: null,
        quantity: Number(form.quantity || 1),
        preferred_delivery_area: form.delivery_area,
        message: body,
        priority: "NORMAL",
      });
      setStatus("Your sourcing request has been sent. You can follow responses from your enquiries workspace.");
      clearPurchaseDraft(REQUEST_DRAFT_KEY);
      setForm({ item: "", category: "", quantity: 1, delivery_area: "", budget: "", message: "" });
    } catch (err) {
      setStatus("Request could not be sent. Please confirm you are logged in as a buyer account.");
    }
  }

  return (
    <div className="page-shell marketplace-page request-page" id="top">
      <MarketplaceHeader apiOnline />
      <main className="container request-page-shell">
        <section className="marketplace-title-banner marketplace-title-banner--request">
          <img src="/images/tazy-source-goods.jpg" alt="" />
          <div className="marketplace-title-banner__content">
            <span>TAZY RFQ</span>
            <h1>Source what you need from verified sellers.</h1>
            <p>Use this when you need stock confirmation, supplier matching, bulk pricing or a seller response before ordering.</p>
          </div>
        </section>

        <section className="request-form-layout request-form-layout-v2">
          <form className="request-form-card request-form-card-v2" onSubmit={submit}>
            <div className="request-form-head"><span>Buyer request</span><h2>Tell sellers what you are looking for</h2></div>
            <label>
              Item or product needed
              <input value={form.item} onChange={(e) => update("item", e.target.value)} placeholder="Example: 200 school bags, laptops, groceries, building materials" />
            </label>
            <div className="request-form-two">
              <label>
                Category
                <select value={form.category} onChange={(e) => update("category", e.target.value)}>
                  <option value="">Select category</option>
                  {categories.map((category) => <option key={category.id || category.code} value={category.id}>{category.name}</option>)}
                </select>
              </label>
              <label>
                Quantity needed
                <input type="number" min="1" value={form.quantity} onChange={(e) => update("quantity", e.target.value)} />
              </label>
            </div>
            <div className="request-form-two">
              <label>
                Delivery area
                <input value={form.delivery_area} onChange={(e) => update("delivery_area", e.target.value)} placeholder="City / suburb / pickup point" />
              </label>
              <label>
                Budget or target price
                <input value={form.budget} onChange={(e) => update("budget", e.target.value)} placeholder="Optional" />
              </label>
            </div>
            <label>
              Extra notes
              <textarea value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="Quality, brand, delivery, payment terms or required deadline" />
            </label>
            {status ? <div className={status.includes("sent") ? "success-box" : "workspace-alert"}>{status}</div> : null}
            <div className="request-action-row">
              <button type="submit">Send sourcing request</button>
              {!isAuthenticated ? <Link onClick={() => savePurchaseDraft(REQUEST_DRAFT_KEY, form)} to={`/login?next=${encodeURIComponent("/request")}`}>Sign in first</Link> : <Link to="/portal/enquiries">View my enquiries</Link>}
            </div>
          </form>
          <aside className="request-guide-card request-guide-card-v2">
            <h3>How this works</h3>
            <ol>
              <li>Buyer submits a request with quantity and delivery area.</li>
              <li>TAZY routes the request to suitable seller workflows.</li>
              <li>Sellers respond with availability and terms.</li>
              <li>Buyer converts the response to an order and tracks fulfilment.</li>
            </ol>
            <Link to="/search?tab=Products">Browse existing products first</Link>
          </aside>
        </section>
      </main>
      <Footer />
      <FloatingTools />
    </div>
  );
}
